// Membership tab — shared fetch helpers, formatters and the API's row shapes (PR A, 2026-09-22).
// docs/MEMBERSHIP_BUILD_PLAN.md. Every list the API returns is coalesced to [] here so a
// handler that drops a field cannot white-screen a page.

export async function getJson<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: 'include' })
  if (!r.ok) {
    const j = (await r.json().catch(() => ({}))) as { error?: string; detail?: string }
    throw new Error(j.error ? `${j.error}${j.detail ? ` — ${j.detail}` : ''}` : `HTTP ${r.status}`)
  }
  return (await r.json()) as T
}

export async function sendJson<T>(url: string, method: 'POST' | 'PUT' | 'DELETE', body?: unknown): Promise<T> {
  const r = await fetch(url, { method, credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: body === undefined ? undefined : JSON.stringify(body) })
  if (!r.ok) {
    const j = (await r.json().catch(() => ({}))) as { error?: string; detail?: string }
    throw new Error(j.error ? `${j.error}${j.detail ? ` — ${j.detail}` : ''}` : `HTTP ${r.status}`)
  }
  return (await r.json()) as T
}

const gbp = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' })
export const money = (v: number | string | null | undefined) => (v === null || v === undefined || v === '' ? '' : gbp.format(Number(v)))
export const int = (v: number | null | undefined) => (v === null || v === undefined ? '' : Number(v).toLocaleString('en-GB'))
export const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return ''
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso))
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(iso)
}
export const fmtDateTime = (iso: string | null | undefined) => (iso ? `${fmtDate(iso)} ${String(iso).slice(11, 16)}` : '')
export const yesNo = (v: boolean | number | null | undefined) => (v === null || v === undefined ? '' : v ? 'Yes' : 'No')
export const prettyUser = (u: string | null | undefined) => (!u ? '' : u.includes('@') ? u.split('@')[0].replace(/\./g, ' ') : u)
export const text = (v: unknown) => (v === null || v === undefined ? '' : String(v))

export interface GridResult<T> { rows: T[]; total: number; page: number; size: number; sort: string; dir: 'asc' | 'desc' }

export interface MemberGridRow {
  member_id: number; membership_id: number | null; ontrack_oid: number | null; person_number: number | null
  title: string | null; first_name: string | null; surname: string | null; known_as: string | null; full_name: string | null
  email: string | null; telephone: string | null; mobile: string | null; postcode: string | null; town: string | null
  membership_card_number: string | null; membership_number: number | null; category: string | null; category_short: string | null
  membership_type: string | null; class: string | null; membership_status: string | null; renewal_date: string | null
  payment_type: string | null; gift_aid: boolean | null; life_member: boolean | null
  status: string; inactive: number; sync_status: string; discrepancies: number; master: string | null
  has_donorfy: number; has_gocardless: number; deceased: boolean | null; gone_away: boolean | null; archived: boolean | null
}

export interface MembershipGridRow {
  membership_id: number; ontrack_oid: number | null; membership_number: number | null; members_names: string | null
  category: string | null; category_short: string | null; type: string | null; class: string | null; source: string | null
  status: string | null; status_code: number | null; start_date: string | null; renewal_date: string | null; last_renewed: string | null
  payment_type: string | null; payment_method: string | null; gift_aid: boolean | null; has_dd: number
  mandate_status: string | null; subscription_status: string | null; postcode: string | null; town: string | null; fee_paid: number | null
  member_count: number; inactive: number; sync_status: string; discrepancies: number; master: string | null
  gone_away: boolean | null; cancellation_reason: string | null; has_gocardless: number
}

// Interactions grid — a membership-card interaction at the tills (reports db/130, from MERAC).
export interface InteractionGridRow {
  interaction_id: number; membership_no: number | null; card_no: string | null; category: string | null; member_name: string | null
  till_location: string | null; employee_name: string | null; visited_at: string | null; visited_on: string | null
  item: string | null; rrp: number | null; qty: number | null; sold_price: number | null
  membership_expiry: string | null; transaction_no: number; trans_seq: number; inactive: number
}

export type MemberRecord = Record<string, unknown> & {
  member_id: number; membership_id: number | null; first_name: string | null; surname: string | null; full_name: string | null
  membership_card_number: string | null; status?: string; inactive?: number; sync_status: string; discrepancies: number; master: string | null
  has_donorfy?: number; has_gocardless?: number
}
export type MembershipRecord = Record<string, unknown> & {
  membership_id: number; membership_number: number | null; members_names: string | null; category: string | null; status: string | null
  sync_status: string; discrepancies: number; master: string | null; member_count?: number; has_dd?: number; inactive?: number
}

export interface Pairing { pairing_id: number; system: 'ontrack' | 'donorfy' | 'gocardless'; external_id: string; external_number: string | null; method: string; confidence: number; paired_by: string | null; paired_at: string }
export interface PaymentsSummary { n: number; total: number | null; last_paid: string | null; gift_aid_estimate: number | null }
export interface Diff { field: string; ours: string | null; theirs: string | null; donorfy?: string | null; differs_ontrack?: boolean | number; differs_donorfy?: boolean | number; ignored_ontrack?: number; ignored_donorfy?: number }

export interface DonorfyPaired {
  constituent_number: number; constituent_id: string | null; display_name: string | null; email: string | null; postcode: string | null; address_line1: string | null; town: string | null
  external_key: string | null; is_active: boolean | null; gift_aid_refused: boolean | null; tags: string | null; lifetime_gifts_amount: number | null; last_gift_date: string | null; date_changed: string | null
}

export interface DeclRow { declaration_id: string; start_date: string | null; end_date: string | null; method: string | null; status: string | null; is_active: boolean | null; date_added: string | null; refreshed_at: string | null }

export interface MemberPayload {
  member: MemberRecord
  membership: MembershipGridRow | null
  joint_members: MemberGridRow[]
  pairings: Pairing[]
  payments: PaymentsSummary
  ontrack_differences: Diff[]
  donorfy?: DonorfyPaired | null
  donorfy_declarations?: DeclRow[]
  history_count: number
}
export interface MembershipPayload {
  membership: MembershipRecord
  members: MemberGridRow[]
  pairings: Pairing[]
  payments: PaymentsSummary
  history_count: number
}

export interface PaymentRow {
  payment_id: number; source: 'ontrack' | 'gocardless' | 'donorfy'; source_key: string; membership_id: number | null; member_id: number | null
  paid_on: string | null; paid_at: string | null; amount: number | null; is_donation: boolean | null; gift_aid: boolean | null
  payment_type: string | null; payment_type_code: string | null; payment_method: string | null; payment_status: string | null
  reference: string | null; narrative: string | null; gocardless_payment_id: string | null; gocardless_status: string | null; charge_date: string | null
  is_collected: boolean | null; channel: string | null; payer_name: string | null; campaign: string | null; fund: string | null; product: string | null
  gift_aid_status: string | null; gift_aid_claimed: number | null; gift_aid_estimate: number | null; membership_number: number | null; member_name: string | null
}
export interface PaymentsPayload { rows: PaymentRow[]; totals: { n: number; total: number | null; collected: number | null; gift_aid_estimate: number | null; gift_aid_claimed: number | null } }

export interface HistoryRow {
  history_id: number; at: string; who: string; entity_type: 'member' | 'membership'; entity_id: number; system: string; action: string
  field: string | null; old_value: string | null; new_value: string | null; batch_id: number | null; change_id: number | null; detail: string | null
  record_name: string | null; record_number: string | null
}
export interface HistoryPayload { rows: HistoryRow[]; total: number; page: number; size: number }

export interface DiscrepancyRow {
  member_id: number; full_name: string | null; membership_card_number: string | null; membership_id: number | null; membership_number: number | null
  field: string; membership_value: string | null; ontrack_value: string | null; donorfy_value: string | null
  differs_ontrack: boolean | number; differs_donorfy: boolean | number; ignored_ontrack: number; ignored_donorfy: number; master: string | null
}
export interface DiscrepanciesPayload { rows: DiscrepancyRow[]; total: number; page: number; size: number; fields: { field: string; n: number }[]; by_system?: { ontrack: number; donorfy: number } }

export interface SyncStatus {
  in_flight: boolean
  latest: { id: number; status: string; requested_by: string; requested_at: string | null; started_at: string | null; completed_at: string | null; message: string | null; duration_ms: number | null } | null
  last_refresh: string | null; last_refresh_status: string | null; last_refresh_message: string | null
  meta: { source: string; refreshed_at: string | null; row_count: number; status: string; message: string | null }[]
  lockout_minutes: number; next_available_at: string | null; locked: boolean; can_request: boolean; jobs: string[]
}

export interface Summary {
  counts: { members: number; members_current: number; members_inactive: number; members_discrepant: number; memberships: number; memberships_current: number; memberships_dd: number; payments: number; history: number; local_only: number }
  meta: { source: string; refreshed_at: string | null; row_count: number; status: string; message: string | null }[]
  pairings: { entity_type: string; system: string; n: number }[]
}

interface Counted { n: number }
export interface Lookups {
  categories: ({ category: string; category_short: string | null } & Counted)[]
  statuses: ({ status: string } & Counted)[]
  membershipStatuses: ({ status: string } & Counted)[]
  types: ({ type: string } & Counted)[]
  classes: ({ class: string } & Counted)[]
  paymentTypes: ({ payment_type: string } & Counted)[]
  paymentTypesPaid: ({ payment_type: string } & Counted)[]
  sources: ({ source: string } & Counted)[]
  syncStatuses: ({ sync_status: string } & Counted)[]
  enums: { enum_name: string; code: number; label: string }[]
  interactionCategories: ({ category: string } & Counted)[]
  interactionLocations: ({ till_location: string } & Counted)[]
  interactionEmployees: ({ employee_name: string } & Counted)[]
}

export interface GridPrefs { sort?: string; dir?: 'asc' | 'desc'; size?: number; filters?: Record<string, string>; q?: string; includeInactive?: boolean }

export const SYNC_LABEL: Record<string, string> = {
  never_pulled: 'Never pulled', in_sync: 'In sync', discrepancies: 'Discrepancies', local_only: 'Local only',
  deleted_in_ontrack: 'Deleted in Ontrack', pending_writes: 'Pending writes', write_failed: 'Write failed',
}
export const FIELD_LABEL: Record<string, string> = {
  title: 'Title', first_name: 'First name', middle_name: 'Middle name', surname: 'Surname', known_as: 'Known as', date_of_birth: 'Date of birth',
  email: 'Email', email2: 'Second email', telephone: 'Telephone', mobile: 'Mobile', address_line1: 'Address line 1', address_line2: 'Address line 2',
  address_line3: 'Address line 3', address_line4: 'Address line 4', town: 'Town', region: 'Region', county: 'County', postcode: 'Postcode', country: 'Country',
  contact_by_post: 'Contact by post', contact_by_phone: 'Contact by phone', contact_by_email: 'Contact by email', contact_by_sms: 'Contact by SMS',
  gone_away: 'Gone away', deceased: 'Deceased',
}
export const fieldLabel = (f: string) => FIELD_LABEL[f] ?? f.replace(/_/g, ' ')
